import React from 'react';
import { styled, css, useTheme } from 'styled-components';

import { useButtonDisabledContext } from '../../Controls/Button/disabled-context.tsx';
import { getColor } from './helpers.ts';
import type { IconAlignedProps, IconRender } from './types.ts';

export const Component = styled.svg<IconAlignedProps>`
  min-width: ${({ width }) => `${width}px`};
  ${({ align }) =>
    align === 'top' &&
    css`
      transform: rotate(0);
    `}
  ${({ align }) =>
    align === 'top-right' &&
    css`
      transform: rotate(45deg);
    `}
  ${({ align }) =>
    align === 'right' &&
    css`
      transform: rotate(90deg);
    `}
  ${({ align }) =>
    align === 'right-bottom' &&
    css`
      transform: rotate(135deg);
    `}
  ${({ align }) =>
    align === 'bottom' &&
    css`
      transform: rotate(180deg);
    `}
  ${({ align }) =>
    align === 'bottom-left' &&
    css`
      transform: rotate(225deg);
    `}
  ${({ align }) =>
    align === 'left' &&
    css`
      transform: rotate(270deg);
    `}
  ${({ align }) =>
    align === 'left-top' &&
    css`
      transform: rotate(315deg);
    `}
`;

interface BaseComponentProps extends IconAlignedProps {
  iconRender: IconRender;
  ref: React.ForwardedRef<SVGSVGElement | null>;
  viewBox?: string;
}

export const BaseComponent = React.memo(function BaseComponent({
  size = 20,
  color: colorProperty,
  className,
  onClick,
  alt,
  dataTestId,
  disabled: disabledProperty,
  ref,
  iconRender,
  align,
  viewBox = '0 0 24 24',
}: BaseComponentProps) {
  const disabled = useButtonDisabledContext();
  const theme = useTheme();

  const color = React.useMemo(
    () =>
      getColor({
        color: !colorProperty && disabled !== undefined ? 'currentcolor' : colorProperty,
        disabled: disabledProperty ?? disabled,
        alt,
        theme,
      }),
    [colorProperty, disabledProperty, alt, theme, disabled],
  );

  return (
    <Component
      align={align}
      className={className}
      onClick={onClick}
      ref={ref}
      width={size}
      height={size}
      viewBox={viewBox}
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      data-testid={dataTestId}>
      {iconRender(color)}
    </Component>
  );
});
